import { AgentRun, Document, DOCUMENT_MIME_TYPES, JSONObject } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { useCreateAgentRun, useCreateDocument, usePutFile } from '@/hooks/api';
import { isInArray } from '@/utils';

export type UseAgentOpts = {
  agentId?: string | null;
};

export const useAgent = ({ agentId: agentIdHookOpt }: UseAgentOpts = {}) => {
  const { mutateAsync: createAgentRun } = useCreateAgentRun();
  const { mutateAsync: createDocument } = useCreateDocument();
  const { mutateAsync: putDocumentFile } = usePutFile();

  const createRun = useCallback(
    ({
      file,
      agentId: agentIdCallbackArg,
      variables,
    }: {
      file: File;
      agentId?: string;
      variables?: JSONObject | null;
    }) => {
      return new Promise<{ run: AgentRun; document: Document; file: { fileUrl: string; blob: Blob } }>(
        (resolve, reject) => {
          const agentId = agentIdCallbackArg ?? agentIdHookOpt;

          if (!agentId) {
            console.error('You must provide agentId');
            reject('You must provide agentId');
            return;
          }

          if (!isInArray(DOCUMENT_MIME_TYPES, file.type)) {
            console.error(`${file.name} has incompatible mime type: ${file.type}`);
            reject(`${file.name} has incompatible mime type: ${file.type}`);
            return;
          }
          createAgentRun({ agentId, variables })
            .then((run) => {
              createDocument({ name: file.name, agentRunId: run.id })
                .then((document) => {
                  putDocumentFile({ fileUrl: document.fileUrl, file })
                    .then((f) => {
                      resolve({ run, document, file: f });
                    })
                    .catch((error) => reject(error.message));
                })
                .catch((error) => reject(error.message));
            })
            .catch((error) => reject(error.message));
        }
      );
    },
    [createDocument, createAgentRun, agentIdHookOpt, putDocumentFile]
  );

  return { createRun };
};
